import { SpirometryAnalysisSetTypeEnum } from "./ApiSpirometryAnalysisSetType";
import { DlcoAnalysisSetTypeEnum } from "./ApiDlcoAnalysisSetType";
import { LungVolumeAnalysisSetTypeEnum } from "./ApiLungVolumeAnalysisSetType";
import { AnalysisSetLinkTypesEnum } from "./AnalysisSetLinkTypes";
import { Phases } from "./ApiPhases";
import { SessionTypes } from "./ApiSessionTypes";
import { PressureAnalysisSetTypeEnum } from "./ApiPressureAnalysisSetTypeEnum";
import { OscillometryAnalysisSetTypeEnum } from "./ApiOscillometryAnalysisSetTypeEnum";
import { EcgAnalysisSet, PefAnalysisSet, Site as NewSite } from "webportalclient";

export enum ParameterCategoryType {
    Default = "Default",
    AnalysisParameter = "AnalysisParameter",
    RepeatabilityParameter = "RepeatabilityParameter",
}

export enum QueryCondition {
    Equal = "Equal",
    NotEqual = "NotEqual",
    LessThan = "LessThan",
    LessThanOrEqual = "LessThanOrEqual",
    GreaterThan = "GreaterThan",
    GreaterThanOrEqual = "GreaterThanOrEqual",
    Contains = "Contains"
}

export type QueryFilterValueType = Array<number | string | boolean>;

export interface QueryFilter {
    Member: string;
    Condition?: QueryCondition;
    Values: QueryFilterValueType;
}

export enum QueryOrderDirection {
    Ascending = "Ascending",
    Descending = "Descending"
}

export interface QueryOrder {
    Member: string;
    Direction: QueryOrderDirection;
}

export interface QueryPage {
    Length: number;
    Current: number;
    Total?: number;
}

export interface QueryError {
    Message: string;
    Exception: string;
}

export interface Query<T> {
    Data?: T[];
    Distinct?: boolean;
    Filters?: QueryFilter[];
    Members?: string[];
    Order?: QueryOrder;
    Page?: QueryPage;
    Count?: number;
    Errors?: QueryError[];
}

export interface ModelError {
    Exception: string;
    Member: string;
    Message: string;
}

export interface ModelWithError {
    Errors?: Array<ModelError>;
}

export function isModelWithError(
    model: Visit | BloodGlucoseSession | ModelWithError | CoughReOpenComments | CoughAnalysisSet | ImportSummaryResult[] | any
): model is ModelWithError {
    if (model === undefined || model === null) return false;
    return (<ModelWithError>model).Errors !== undefined;
}

export interface Study {
    OldId: number;
    Id: string;
    ProtocolNumber: string;
    Name: string;
    Countries: Array<Country>;
    SessionTypes: Array<SessionType>;
    VisitSchedule: Array<VisitScheduleItem>;
}

export interface Site {
    Id: string;
    StudyId: string;
    SiteNumber: string;
    CountryId: string;
    PrimaryContact: string;
}

export interface Subject {
    Id: string;
    Study?: Study;
    StudyId: string;
    SiteNumber: string;
    SiteId: string;
    Site?: Site;
    SubjectNumber: string;
    SubjectTypeName: string;
    RandomisedStatus?: boolean;
    RandomisedDateTime?: string;
    RandomisedId?: string;
    Gender?: Gender;
    Age?: number;
    SubjectType?: SubjectType;
    CreatedWithSpirotracV?: boolean;
    FirstName?: string;
    MiddleName?: string;
    LastName?: string;
    BirthDate?: string;
    PopulationGroupId?: string;
    PopulationGroupName?: string;
    SelfAssessedPopulationGroup?: string;
    Language?: Language;
    SmokerType?: SmokingStatus;
    Height?: number;
    Weight?: number;
    BMI?: number;
    Bedtime?: number;
    EdiarySubject?: EDiarySubject;
    TerminationDate?: string
}

export interface EDiarySubject {
    Pin: string;
    Alarms: string[];
}

export interface Gender {
    Id: string;
    Name: string;
}

export interface SubjectType {
    Id: SubjectTypes;
    Name: string;
}

export interface Language {
    Id: string;
    Name: string;
}

export interface SmokingStatus {
    Id: string;
    Name: string;
}

export interface CoughProcessingAlgorithms {
    Id: number;
    Name: string;
}

export interface SettingSectionDefinition {
    path: string;
    value: any;
}

export interface SettingDefinition {
    path: string;
    value: any;
    type: SettingType;
    label: string;
    valueType: SettingValueType;
    defaultValue: any;
    min?: number;
    max?: number;
    dependsOn?: string;
    dependsOnValue?: object;
}

export enum SettingType {
    String = "String",
    MultiLineString = "MultiLineString",
    Boolean = "Boolean",
    Object = "Object",
    Number = "Number",
    Time = "Time",
    Enum = "Enum"
}

export enum SettingValueType {
    One = "One",
    Collection = "Collection"
}


export interface SessionType {
    Id: SessionTypes;
    Name: string;
}

export enum SubjectTypes {
    InStudy = "STATE_INSTUDY",
    Training = "STATE_TRAINING",
    Certification = "STATE_CERT"
}

export interface Visit {
    Id: string;
    Name: string;
    SubjectId: string;
    VisitDateTime: string;
    VisitScheduleItemId: string;
}

export interface Session {
    Id: string;
    Name: string;
    StudyId: string;
    Study?: Study;
    SiteId: string;
    Site?: Site;
    SubjectId: string;
    Subject: Subject;
    VisitId: string;
    Visit?: Visit;
    SerialNumber: string;
    SessionType?: SessionType;
    SessionTypeId: SessionTypes;
    SessionScheduleId: string;
    DeviceTypeId: DeviceTypes;
    Technician?: string;
    OverreadStatusId?: OverreadStatusIdEnum;
}

export interface CoughSession extends Session {
    ConfigurationTechnician: string;
    UploadTechnician: string;
    Acceptability?: string;
    StatusId: CoughSessionStatusEnum;
    MediaSerialNumber: string;
    PrimaryWaveformType?: string;
    CreatedDateTime: string;
    LastReceivedDateTime: string;
    ProcessedDateTime: string;
    RecordingStartDateTime: string;
    UploadEndTime: string;
    StatusName: string;
    PostalRecord?: CoughSessionPostalRecord;
    LastAnalysedDateTime?: string;
    CoughAnalysisSets?: Array<CoughAnalysisSet>;
    IsSuppressed: boolean;
    IsQARequired: boolean;
    RawDuration: number;
    ProcessedDuration: number;
    SensorProcessedDuration: number;
    UploadRecordingRetry: number;
}

export interface CoughSessionStatus {
    Id: string;
    Name: string;
}

export interface CoughSessionPostalRecord {
    Id: string;
    TrackingNumber: string;
    ReceivedDateTime?: string;
}

export function isCoughSession(model: Session): model is CoughSession {
    if (!model) return false;

    return (<CoughSession>model).SessionTypeId === "ST_COUGH";
}

export enum CoughSessionStatusEnum {
    AwaitingData = 'CSS_AWAITING_DATA',
    ReceivingData = 'CSS_RECEIVING_DATA',
    AwaitingProcessing = 'CSS_AWAITING_PROCESSING',
    Processing = 'CSS_PROCESSING',
    AwaitingQualityCheck = 'CSS_AWAITING_QC',
    AwaitingAnalysis = 'CSS_AWAITING_ANALYSIS',
    AnalysisInProgress = 'CSS_ANALYSIS_WIP',
    AnalysisComplete = 'CSS_ANALYSED',
    DeviceUnassigned = 'CSS_DEVICE_UNASSIGNED',
    QualityCheckRejected = 'CSS_QC_REJECTED',
    ReceivingDataMailed = 'CSS_RECEIVING_DATA_MAILED',
    ProcessingFailed = "CSS_PROCESSING_FAILED"
}

export interface BloodGlucoseSession extends Session {
    Technician: string;
    ReceivedDateTime: string;
    StartDateTime: string;
    SoftwareVersion: string;
    Status: string;
    StatusId: string;
}

export interface BloodGlucoseTest {
    Id: string;
    SessionId: string;
    SampleMethodId: string;
    SampleMethod: BloodGlucoseSampleMethod;
    TestDateTime: string;
    TestNumber: string;
    BloodGlucoseMmol: number;
    BloodGlucoseMgdl: number;
    BloodKetoneMmol: number;
}

export enum BloodGlucoseSessionStatusEnum {
    AwaitingData = "BGSS_AWAITING_DATA",
    ReceivedData = "BGSS_RECEIVED_DATA",
}

export interface EcgSession extends Session {

}

export interface CoughAnalysisSet {
    OldId?: number;
    Id?: string;
    SessionId: string;
    TypeId: string;
    Type?: CoughAnalysisSetType;
    CreatedDateTime?: string;
    ModifiedDateTime?: string;
    StartedDateTime?: string;
    StatusId?: string;
    Status?: OverreadStatus;
    ProcessingComplete?: boolean;
    Study?: Study;
    Site?: Site;
    Subject?: Subject;
    Visit?: Visit;
    Session?: CoughSession;
    Analyst?: User;
    HasReOpenComments?: boolean;
    ReviewRequired: boolean;
    ReviewComplete: boolean;
    ReOpenedDuringReview: boolean;
    LinksSourcedFromThis: Array<any>;
    LinksTargetingThis: Array<any>;
    IsLinkedAnalysisSet: boolean;
    IsSourceOfAnother: boolean;
    AdjudicationSourceSetId: string;
    AdjudicationTargetSetId: string;
}

export interface AnalysisSetAccess {
    CanEdit: boolean;
}

export interface CoughAnalysisSetsFixedCommentsDisplay extends CoughAnalysisSetFixedComments {
    DisplayOrder: number;
}

export interface CoughAnalysisSetFixedComments extends Pick<CoughAnalysisSet, "Id" | "SessionId" | "Type" | "StatusId" | "CreatedDateTime" | "ModifiedDateTime" | "Analyst" | "IsSourceOfAnother"> {
    FixedCommentIds: Array<string>;
    IsSelected: boolean;
}

export interface CoughAnalysisSetType extends AnalysisSetType {
    IsQA: boolean;
}

export interface AnalysisSetType {
    Id: string;
    Name: string;
}

export interface CoughAnalysisSetEvent {
    Id?: string;
    AnalysisSetId: string;
    EventTypeId: string;
    RawStart?: number;
    RawEnd?: number;
    ProcessedStart?: number;
    ProcessedEnd?: number;
    SensorProcessedStart?: number;
    SensorProcessedEnd?: number;
    VtaCt1Start?: number;
    VtaCt1End?: number;
    WithinDuplicateThreshold?: boolean;
    SegmentOffset?: number;
    Disputed?: boolean;
    DisputedAccepted?: boolean;
    DisputedComment?: string;
}

export interface CoughAnalysisSetComment {
    id?: string;
    analysisSetId?: string;
    comment?: string;
    commentStartTime?: number;
    commentEndTime?: number;
    userEmail?: string;
    created?: string;
    parentCommentId?: string;
}

export interface StudyCoughEventType {
    Index: number;
    Id: string;
    StudyId: string;
    EventName: string;
    EventOrder: number;
    Colour: string;
    Hidden: boolean;
    IsCounted: boolean;
    IsSegmented: boolean;
    AssociatedDeviceButton: number;
}

export interface StudyOverreadFixedCommentSet {
    Id: string;
    StudyId: string;
    Study: Study;
    SessionTypeId: SessionTypes;
    SessionSetId: string;
    SessionSet: OverreadFixedCommentSet;
    TestSetId: string;
    TestSet: OverreadFixedCommentSet;
    DefaultSessionAcceptableCommentId: string | null;
    DefaultTestAcceptableCommentId: string | null;
}

export interface OverreadFixedCommentSet {
    Id: string;
    SessionTypeId: string;
    SessionType: SessionType;
    Comments: Array<OverreadFixedComment>;
}

export interface OverreadFixedComment {
    Index: number;
    Id: string;
    SetId: string;
    Mask: number;
    Comment: string;
    Description: string;
    DisplayOrder: number;
    IsDisabled: boolean;
    IsSelected: boolean;
}

/**
 * Comments used to allow re-opening of a cough session
 * */
export interface CoughReOpenComments {
    ReOpenCommentGroups: CoughReOpenCommentGroup[];
}

export interface CoughReOpenCommentGroup {
    Order: number;
    Comments: string[];
    SelectedOption: string;
    GroupName: string
}

export interface CoughAnalysisSetReOpenComment {
    Comment: string;
    GroupName: string;
}

export interface CoughDeadlinesModel {
    QaDeadline: string;
    AnalysisDeadline: string;
    Annotation: string
}

export interface CoughWaveformType {
    Index: number;
    Id: string;
    Name: string;
}

export interface Permission {
    Id: string;
    Name: string;
    StudyId: string;
}

export enum OverreadStatusEnum {
    NotStarted = 1,
    Pending = 2,
    Accepted = 3,
    Rejected = 4
}

// HACK: Used for newer API calls that no longer reference the numeric ID for enums.
export enum OverreadStatusIdEnum {
    NotStarted = "ORS_NOT_STARTED",
    Pending = "ORS_PENDING",
    Accepted = "ORS_ACCEPTED",
    Rejected = "ORS_REJECTED"
}

export enum CorrectionTypesEnum {
    SessionSuppress = 'CR_SESSION_SUPPRESS',
    SubjectType = 'CR_SUBJECT_TYPE',
    SubjectNumber = 'CR_SUBJECT_NUMBER',
    RecordingStart = 'CR_RECORDING_START',
    MediaSerial = 'CR_MEDIA_SERIAL',
    MoveSession = 'CR_MOVE_SESSION',
    SwapLabels = 'CR_SWAP_LABELS',
    Miscellaneous = 'CR_MISCELLANEOUS',
}

export enum AnalysisSetReviewStatuses {
    ReviewComplete = "ReviewComplete",
    ReviewRequired = "ReviewRequired",
    ReOpenedDuringReview = "ReOpenedDuringReview"
}

export enum AnalystDecisionStatus {
    NoChanges = 0,
    ApplyChangesAccept = 1,
    ApplyChangesFurtherReviewRequired = 2
}

export interface OverreadStatus {
    Id: string;
    Name: string;
}

export interface BloodGlucoseSampleMethod {
    Id: string;
    Name: string;
}

export interface StudyQAConfiguration {
    Id: string;
    SelectionThresholdPercentage: number;
}

export interface StudyQAMetric {
    Id: string;
    StudyQAConfigurationId: string;
    AnalysisSetTypeId: string;
    AnalysisSetType: CoughAnalysisSetType;
}

export interface StudyQAMetricRun {
    Id: string;
    QAMetricId: string;
    QAMetric: StudyQAMetric;
    RunTime: string;
    SessionsSelectedCount: number;
}

export interface Role {
    Id: string;
    Name: string;
    Study: Study;
    ChildRolesIds: Array<string>;
}

export interface User {
    Id: string;
    OldId: number;
    Username: string;
    Email: string;
    FirstName: string;
    LastName: string;
    Roles: Array<Role>;
    Sites: Array<Site>;
    IsSpirotracEnabled: boolean;
    IsServiceAccount: boolean;
    HasEDiaryPIN: boolean;
    TimeZoneId: string;
    IsDisabled: boolean;
}

export interface ReportingServicesCatalogItem {
    Id: string;
    Name: string;
    Type: 'Report' | 'DataSource';
    Path?: string;
    Created?: string;
    Modified?: string;
}

export interface ImportSummaryResult {
    ReportName: string;
    IsSuccess: boolean;
    Message: string;
}

export interface Report extends ReportingServicesCatalogItem {
    IsRequestingDownload: boolean;
    Roles: any[];
}

export interface ReportWidget {
    Id: string;
    Name: string;
    Server: string;
    Path: string;
    Session: string;
    Parameters: { [key: string]: string };
}

export interface SSRSCredential {
    ServerURL: string;
    Username: string;
    Password?: string;
    PasswordEncrypted?: string;
    ServerType: number
}

export interface Note {
    Id: string;
    Username: string;
    CreatedDate: string;
    Text: string;
}

export interface SubjectNote extends Note {
    SubjectId: string;
}

export interface SessionNote extends Note {
    SessionId: string;
}

export interface FileModel {
    Id: string;
    FileName: string;
    StudyIds: string[];
}

export enum DeviceTypes {
    Pneumotrac = "DT_MODEL_6800",
    In2itive = "DT_IN2ITIVE",
    Alpha = "DT_ALPHA",
    Asma1 = "DT_ASTHMA1",
    In2itiveEDiary = "DT_EDIARY",
    ADBloodPressureMonitor = "DT_AND_767PC",
    ADBloodPressureMonitorBluetooth = "DT_AND_767PLUSBT",
    Spirotrac5 = "DT_SPIROTRAC_V",
    Spirotrac6 = "DT_SPIROTRAC_VI",
    In2itiveP230EDiary = "DT_P230_EDIARY",
    VitaloJAK2 = "DT_VITALOJAK",
    VitaloJAK1 = "DT_VITALOJAK_I",
    StatStripXpress2 = "DT_STATSTRIP_XPRESS_2",
    MobileApp = "DT_MOBILE_APP"
}

export interface DeviceType {
    Id: DeviceTypes;
    Name: string;
}

export enum DeviceInteractions {
    Upload = "DI_UPLOAD",
    Mail = "DI_MAIL",
    ResetDevice = "DI_RESET_DEVICE",
    CalibrationVerification = "DI_CALIBRATION_VERIFICATION"
}

export interface DeviceInteraction {
    Id: string;
    Name: string;
    Description: string;
    Icon: string;
}

export interface DeviceTypeInteraction {
    Id: string;
    DeviceInteraction: DeviceInteraction;
    DeviceType: DeviceType;
}

export interface AvailableDeviceType {
    Key: string;
    Name: string;
    Description: string;
}

export interface Setting<ValType = any> {
    Key: string;
    Value: ValType;
    StudyId: string;
}

export enum ChallengeDecisionMaker {
    Overreader = "Overreader",
    Technician = "Technician"
}

export enum IndividualAcceptabilityStatus {
    Hidden = "Hidden",
    ReadOnly = "ReadOnly",
    Overreading = "Overreading"
}

export interface StudyFvcSetting {
    TestAcceptanceChange: boolean;
    TestAcceptanceChangeTimeWindow: number;
    AtsDeviationsFvc: boolean;
    AtsDeviationsFvcVolume?: number;
    AtsDeviationsFev1: boolean;
    AtsDeviationsFev1Volume?: number;
    AcceptableTests: number;
    MaxTests: number;
    IsOverreadingAvailable: boolean;
    GradingEnabled: boolean;
    SelectedTestParameters: number[];
    AtsGuidelineRevision: string;
    SelectedFinalTestAcceptabilityDecisionMaker: ChallengeDecisionMaker;
    SelectedAnalysisParameters: number[];
    PredictedValueCalculationsEnabled: boolean;
    SelectedRepeatabilityParameters: number[];
    TechnicianChallengeOverreaderDecision: boolean;
    HomeSpirometryInstructions: string;
    IndividualAcceptabilitiesEnabled: IndividualAcceptabilityStatus;
}

export interface StudyPressureSetting {
    TestAcceptanceChange: boolean;
    TestAcceptanceChangeTimeWindow: number;
    AcceptableTests: number;
    MaxTests: number;
    IsOverreadingAvailable: boolean;
    SelectedTestParameters: number[];
    SelectedFinalTestAcceptabilityDecisionMaker: ChallengeDecisionMaker;
    SelectedAnalysisParameters: number[];
    SelectedRepeatabilityParameters: number[];
}

export interface StudyLungVolumeSetting {
    AcceptableTests: number;
    MaxTests: number;
    AcceptableERVTolerance: number;
    IsOverreadingAvailable: boolean;
    SelectedTestParameters: number[];
    SelectedAnalysisParameters: number[];
    SelectedRepeatabilityParameters: number[];
}

export interface StudyPulseOximetrySetting {
    MaximumRecordingTime: number;
    DisplayAsDuration: boolean;
    DisplayMedianValues: boolean;
    BpmMaximum: number;
}

export interface StudyDcfSetting {
    SystemWorkflow: DcfSystemWorkflow;
    EnabledDcfTypes: string[];
}

export enum DcfSystemWorkflow {
    Log = "DSW_LOG",
    Implementation = "DSW_IMPLEMENTATION",
    Dcf360 = "DSW_360"
}

export interface Phase {
    Id: Phases;
    Name: string;
    DisplayOrder: number;
}

export interface VisitScheduleItem {
    Id: string;
    VisitScheduleId: string;
    Label: string;
    Description: string;
    CanSpanDays: boolean;
    CommentRequired: boolean;
    ConfirmationRequired: boolean;
    Obligation: string;
    Disabled: boolean;
    Phase: Phase;
    PhaseId?: string;
    DisplayOrder: number;
    RequiredVisits: string[];
    BlockedByVisits: string[];
    Sessions: Array<EDiarySessionScheduleItem>;
    StudyId: string;
    Cohort: string;
}

export interface EDiaryVisitScheduleItem extends VisitScheduleItem {
    Sessions: Array<EDiarySessionScheduleItem>;
}

export interface SessionScheduleItem {
    Id: string;
    Label: string;
    Rules?: string;
    Description?: string;
    DeviceType: DeviceType;
    DeviceTypeId: DeviceTypes;
    DisplayOrder: number;
    VisitScheduleItemId: string;
    VisitScheduleItem: VisitScheduleItem;
    SessionTypeId: SessionTypes;
    StudyId: string;
    PreSessionItemId?: string;
    IsMandatory: boolean;
    Disabled: boolean;
    Rolling: boolean;
    ContinueTest: boolean;
    Group: string;
    RequiredSessions: any[];
    Cohort: string;
    AfterRandomisation: boolean;
    VisualReview?: boolean;
    RequireDoseTime: boolean;
    Tags: string;
    Unscheduled: boolean;
    ProcessingAlgorithms?: string[];
}

export interface SessionScheduleAction {
    Id: string;
    ActionType: number;
    ActionValue: string;
    DisplayOrder: number;
}

export interface EDiarySessionScheduleItem extends SessionScheduleItem {
    StartOfWindow: string;
    EndOfWindow: string;
    ResetDiaryOnUpload: boolean;
    IncludedInDailySummaryCompliance?: boolean;
    Configuration?: string;
    ExpectedMinimumQuestionnaires?: number;
    ExpectedMinimumTests?: number;
}

export interface Country {
    Id: string;
    LocalMediaPostalAddress: string;
    SupportToolFreeNumber: string;
}

export interface DcfCorrection {
    Id: string;
    RequestId: string;
    Entity: string;
    Field: string;
    OriginalValue: string;
    CorrectedValue: string;
}

export interface DcfRequest extends ModelWithError {
    Id?: string;
    IdGuid?: string;
    EntityId?: number;
    PIName?: string;
    ChangeBackground: string;
    OriginalEntry: string;
    NewEntry: string;
    SiteId?: string;
    Site?: Site;
    SessionId?: string;
    Session?: Session;
    SessionLabelIncludingAll?: string;
    AllSessions: boolean;
    SubjectId?: string;
    Subject?: Subject;
    SubjectNumberIncludingAll?: string;
    VisitId?: string;
    Visit?: Visit;
    VisitNameIncludingAll?: string;
    AllVisits: boolean;
    StatusId?: DcfRequestStatusEnum;
    CreatedDateTime?: string;
    CreatedByUser?: string;
    AuditAnnotation?: string;
    SiteApprovedDateTime?: string;
    SiteApprovedByUser?: string;
    SiteApprovedAnnotation?: string;
    VitalographApprovedDateTime?: string;
    VitalographApprovedByUser?: string;
    VitalographApprovedAnnotation?: string;
    ActionedDateTime?: string;
    ActionedByUser?: string;
    ActionedApprovedAnnotation?: string;
    Corrections: Array<DcfCorrection>;
}

export interface DcfRequestStatus extends ModelWithError {
    Id: string;
    Name: string;
    Order: number;
}

export enum DcfRequestStatusEnum {
    SiteApproval = "DCF_SITE_REVIEW",
    VerificationReview = "DCF_VERIFICATION_REVIEW",
    VitalographApproval = "DCF_VITALOGRAPH_APPROVED",
    Implemented = "DCF_IMPLEMENTED",
    Approved = "DCF_APPROVED",
    Rejected = "DCF_REJECTED",
    RejectedWithSuggestions = "DCF_REJECTED_WITH_SUGGESTIONS"
}

export interface FvcFlow {
    SessionId: number;
    Tests: Array<FvcTestFlow>;
}

export interface SvcFlow {
    SessionId: string;
    Tests: Array<SvcTestFlow>;
}

export interface PressureFlow {
    SessionId: number;
    Tests: Array<PressureTestFlow>;
}

export interface FvcTestFlow {
    TestId: string;
    TestNumber: number;
    ColorHex: string;
    IsDisplayed: boolean;
    VolumeData: Array<FvcDataPoint>;
}

export interface SvcTestFlow {
    TestId: string;
    TestNumber: number;
    ColorHex: string;
    IsDisplayed: boolean;
    Vc: number;
    VolumeData: Array<SvcDataPoint>;
}

export interface PressureTestFlow {
    TestId: string;
    TestNumber: number;
    ColorHex: string;
    IsDisplayed: boolean;
    PressureData: Array<PressureDataPoint>;
}

export interface FvcDataPoint {
    Time: number;
    Flow: number;
    Volume: number;
}

export interface SvcDataPoint {
    Time: number;
    Flow: number;
    Volume: number;
}

export interface PressureDataPoint {
    Time: number;
    Pressure: number;
}

export interface SessionMetadata {
    Id: string;
    StudyName: string;
    SiteId: string;
    SiteName: string;
    SiteCountryName: string;
    SiteCountryCode: string;
    ProtocolNumber: string;
    SiteNumber: string;
    Technician: string;
    SubjectId: string;
    SubjectNumber: string;
    VisitName: string;
    SessionName: string;
    SessionDateTime?: string;
    ReceivedDateTime?: string;
    SessionTypeName: string;
    SessionTypeId: string;
    NumTests?: string;
    Status: string;
    StatusId: string;
    Overreader: string;
    ChallengeStatusId?: string;
    ChallengeIsClosed?: boolean;
    Hb?: number;
    Age: number;
    AnalysisSets: (SpirometryAnalysisSet | DlcoAnalysisSet | LungVolumeAnalysisSet | SvcAnalysisSet | PressureAnalysisSet | OscillometryAnalysisSet)[];
}

export interface SpirometrySessionMetadata extends SessionMetadata {
    ChallengeStatus: string;
    ChallengeStatusId: string;
    AnalysisSets: SpirometryAnalysisSet[];
}

export interface DlcoSessionMetadata extends SessionMetadata {
    AnalysisSets: DlcoAnalysisSet[];
}

export interface SvcSessionMetadata extends SessionMetadata {
    AnalysisSets: SvcAnalysisSet[];
}

export interface AnalysisSet {
    Id: string;
    SessionId: string;
    SessionLabel: string;
    StatusId: string;
    Status: OverreadStatus;
    CreatedDateTime: string;
    ModifiedDateTime?: string;
    StartedDateTime?: string;
    Study: Study;
    Site: Site;
    Subject: Subject;
    Visit: Visit;
    Analyst?: User;
    Technician: string;
    SessionDateTime: string;
}

export interface ChallengeableAnalysisSet extends AnalysisSet {
    ChallengeStatusId: string;
    ChallengeStatus: string;
    ChallengeIsClosed: boolean;
}

export interface SpirometryAnalysisSet extends ChallengeableAnalysisSet {
    Type: SpirometryAnalysisSetTypeModel;
    ChallengeStatusId: string;
    ChallengeStatus: string;
    ChallengeIsClosed: boolean;
}

export interface SvcAnalysisSet extends AnalysisSet {
    Type: SvcAnalysisSetTypeModel;
}

export interface DlcoAnalysisSet extends ChallengeableAnalysisSet {
    Type: DlcoAnalysisSetTypeModel;
}


export interface OscillometryAnalysisSet extends AnalysisSet {
    Type: OscillometryAnalysisSetTypeModel;
}

export interface LungVolumeAnalysisSet extends AnalysisSet {
    Type: LungVolumeAnalysisSetTypeModel;
}

export interface PressureAnalysisSet extends AnalysisSet {
    Type: PressureAnalysisSetTypeModel;
}

export interface SpirometryAnalysisSetTypeModel {
    Id: SpirometryAnalysisSetTypeEnum;
    Name: string;
}

export interface SvcAnalysisSetTypeModel {
    Id: SpirometryAnalysisSetTypeEnum;
    Name: string;
}

export interface DlcoAnalysisSetTypeModel {
    Id: DlcoAnalysisSetTypeEnum;
    Name: string;
}

export interface OscillometryAnalysisSetTypeModel {
    Id: OscillometryAnalysisSetTypeEnum;
    Name: string;
}


export interface LungVolumeAnalysisSetTypeModel {
    Id: LungVolumeAnalysisSetTypeEnum;
    Name: string;
}

export interface PressureAnalysisSetTypeModel {
    Id: PressureAnalysisSetTypeEnum;
    Name: string;
}

export interface TestAcceptabilityMutation {
    TestId: string;
    Accepted: boolean;
}

export interface FixedCommentMutation {
    CommentId: string;
    IsSelected: boolean;
}

export interface OverreadAnalysisSet {
    OverreaderUserId?: string;
    OverreaderUserName?: string;
    LastActivityDateTime?: string;
    OriginalActivityDateTime?: string;
    OverreadStatusId: string;
    OverreadStatusName: string;
    Tests: Array<GenericTest>;
    AnalysisSetFixedComments: Array<OverreadFixedComment>;
}

export interface GenericTest {
    TestId: string;
    IsAcceptedByTechnician: string;
    Overreading?: OverreadTest;
}

export interface OverreadTest {
    IsAcceptedByOverreader: string;
    TestFixedComments: Array<OverreadFixedComment>;
}

export interface AuditTrailEvent {
    Id: string;
    EventDateTime: string;
    SummaryText: string;
    ActionName: string;
    ActionDescription: string;
    User?: User;
    UserAnnotation?: string;
    SignIn: AuditTrailSignIn;
    Entities: Array<AuditTrailEntity>;
    StudyId?: number;
    SiteId?: number;
    SubjectId?: number;
    VisitId?: number;
    SessionId?: number;
    AdditionalDetailsText: string;
    ActionId: string;
}

export interface AuditTrailSignIn {
    IPAddress: string;
    UserAgent: string;
    SignInDateTime: string;
    SignOutDateTime: string;
}

export interface AuditTrailEntity {
    Id?: number;
    RecordId?: number;
    TypeName: string;
    Event?: AuditTrailEvent;
    ActionDescription: string;
    Details: Array<AuditTrailEntityDetail>;
}

export interface AuditTrailEntityDetail {
    PropertyName: string;
    OriginalValue: string;
    NewValue: string;
}

export interface AuditTrailAction {
    Id: string;
    Name: string;
}

export interface TimeZone {
    Id: string;
    DisplayName: string;
    SupportsDaylightSavingTime: boolean;
    SortOrder: number;
    OffsetInMinutes: number;
}

export interface AlertNotification {
    Id: string;
    Headline: string;
    TriggeredDateTime: string;
    TriggeredBySessionId: string;
    StudyId: string;
    StudyName: string;
    TriggeredByDeviceMetadata: string;
    Content: string;
}

export interface UnitType {
    Id: number;
    Name: string;
}

export enum DataItemImportance {
    Mandatory = "MANDATORY",
    Optional = "OPTIONAL",
    NotApplicable = "NOT_APPLICABLE"
}

//TODO: These should be generated from the server enum
export enum DataItemFormat {
    Numeric = "NUMERIC",
    Alphanumeric = "ALPHANUMERIC",
    Custom = "CUSTOM"
}

export interface VitaloJakCardAvailablity {
    Serial: string;
    Available: boolean;
}

export interface File {
    Id: string;
    FileName: string;
    StudyIds: string;
}

export interface AnalysisBatch {
    StudyId: string;
    Id: string;
    Name: string;
    CreatedDateTime: string;
    ModifiedDateTime: string;
}

export interface OverreadChallengeStatus {
    Id: string;
    Name: string;
}

export enum OverreadChallengeStatusEnum {
    NotChallenged = 'ORS_NOT_CHALLENGED',
    Challenged = 'ORS_CHALLENGED'
}

export interface StudyPopulationGroup {
    Id: string;
    StudyId: string;
    Name: string;
    IsDisabled: boolean;
}

export interface Device {
    DeviceTypeId: string;
    SerialNumberPrefix: string;
    DeviceSerialNumber: string;
    CreatedDateTime: string;
}

export interface DeviceAllocation {
    Id: string;
    Subject: Subject;
    Site: Site;
    DeviceSerialNumber: string;
    DeviceType: string;
    AllocatedSince: string;
}

export interface EDiaryConfigurationFile {
    Id: string;
    Name: string;
    FriendlyName: string;
    CreatedDateTime: string;
    LastModifiedDateTime?: string;
    IsStrings: boolean;
}

export interface EDiaryConfiguration {
    Id?: string;
    Name: string;
    Description: string;
    CreatedDateTime?: string;
    LastModifiedDateTime?: string;
    Files?: EDiaryConfigurationFile[];
}

export interface DevicesSpirotracVersionPatchModel {
    Studies: string[];
    Sites: string[];
    Countries: string[];
    Version: string
}

export interface StudySvcSetting {
    TestAcceptanceChange: boolean;
    TestAcceptanceChangeTimeWindow: number;
    MaxTests: number;
    SelectedTestParameters: number[];
    IsOverreadingAvailable: boolean;
    SelectedAnalysisParameters: number[];
    SelectedRepeatabilityParameters: number[];
    SelectedFinalTestAcceptabilityDecisionMaker: ChallengeDecisionMaker;
}

export interface StudyPcfSetting {
    TestAcceptanceChange: boolean;
    TestAcceptanceChangeTimeWindow: number;
    MaxTests: number;
    SelectedTestParameters: number[];
}

export interface StudyEcgSetting {
    MaximumRecordings: number;
    SelectedTestParameters: number[];
    RecordingDuration: number;
    AnalysisQTCParameterKey: number;
    IsPacemakerDetectOn: boolean;
    IsExternallyHandledOverreading: boolean;
}

export interface StudyDlcoSetting {
    DisableDlcoOverreadWithoutHB: boolean;
    DisplayHbParameterInDlcoOverreadingScreen: boolean;
    DlcoUnits: UnitType[];
    KcoUnits: UnitType[];
    DlcoDisplayUnits: string[];
    KcoDisplayUnits: string[];
    AggregateCalculationLimit: number;
    DlcoTestTolerance: number;
    DlcoBestTestCalculationMethod: number;
    KcoBestTestCalculationMethod: number;
    VABestTestCalculationMethod: number;
    SelectedAnalysisParameters: number[];
    SelectedRepeatabilityParameters: number[];
}

export interface StudyDiarySetting {
    MinMaxAlarms: { Min: number, Max: number };
    PefBaselineCalculationMethod: string;
    DeviceParameters: number[];
    DailyPefDeclineAlertThreshold: number;
    DefaultEdiaryAlarm: string[];
    DisplayQuestionnaireScoreSetting: boolean;
    DiaryGrades: boolean;
    StartVisitFromEveningWindowSetting: boolean;
    DisplayPefTrend: boolean;
    DisplayFev1Trend: boolean;
    DisplayBaselines: boolean;
    DisplayCompliance: boolean;
    QualityFlagsEnabled: string[];
}

export interface StudyCoughSetting {
    RecordingDuration: number;
    MultipleRecordingsEnabled: boolean;
    VisualReviewMandatorySetting: StudyVisualReviewMandatorySetting;
    VisitSelection: boolean;
    IsSecureDigitalCard: boolean;
    RecordingTags: string;
    ButtonsEnabled: boolean[];
    MinimumRecordingDuration: number;
    DisplayRandomisationColumn: boolean;
    ProcessingAlgorithms: number[];
    BoutWindowDuration: number;
    EventDuplicateThreshold: number;
    CoughAdjudicationWorkflowEnabled: boolean;
}

export interface StudyVisualReviewMandatorySetting {

    VisualReviewMandatoryQc: boolean;
    VisualReviewMandatoryProjectQa: boolean;
    VisualReviewMandatoryMonitoringQa: boolean;
    VisualReviewMandatoryDifficultCougherQa: boolean;
    VisualReviewMandatoryWeeklyQa: boolean;
}

export interface StudySubjectSetting {
    canEarlyTerminateSubject: boolean;
    limitSubjectLanguageSelectionToSiteCountry: boolean;
}

export interface TableParameters {
    DefaultPageSize: number;
}

export interface StudyPefSetting {
    TestAcceptanceChange: boolean;
    TestAcceptanceChangeTimeWindow: number;
    MaxTests: number;
    SelectedTestParameters: number[];
    SelectedAnalysisParameters: number[];
    SelectedRepeatabilityParameters: number[];
    IsOverreadingAvailable: boolean;
    SelectedFinalTestAcceptabilityDecisionMaker: ChallengeDecisionMaker;
}

export enum SessionScheduleActionTypes {
    Questionnaire = 0,
    Alert = 1,
    Spirometry = 2,
    Compliance = 3,
    Transmission = 4,
    UnsupervisedSpirometry = 5,
    SupervisedSpirometry = 6
}

export enum HomeSpirometryStatus {
    InProgress = "FVCS_INPROGRESS",
    Completed = "FVCS_COMPLETED",
}

export enum VisitObligationEnum {
    CanSkip = "VO_CAN_SKIP",
    CanSkipWithConfirmation = "VO_CAN_SKIP_WITH_CONFIRMATION",
    CannotSkip = "VO_CANNOT_SKIP"
}

export enum CoughEventTypeEnum {
    CoughPeak = "CE_COUGH_PEAK",
    Sleep = "CE_SLEEP",
    CoughBout = "CE_COUGH_BOUT"
}

export enum CoughProcessingAlgorithmEnum {
    Manchester = 1,
    ABACAS = 2,
    TimeBasedBout = 3,
    VtaCt1 = 4,
    ABACASCt1 = 5
}
